import { Lender } from '@1delta/lender-registry'
import {
  isGearboxV3,
  isMidnight,
  isTerm,
  isExactly,
  isLiquityFamily,
  isRiver,
  isTeller,
  isTermMax,
  isUsdd,
  isSky,
  isDssType,
  isFrankencoin,
  isLlamaLend,
  isMorphoType,
  LIQUITY_FAMILY_LENDERS,
  isSiloV2Type,
  isSiloV3Type,
} from '@1delta/lender-registry'
import { BaseYields, OpenParameter, RewardsList } from './apiReturnType'
export type SerializedBigNumber = string
export type SerializedNumber = string

export interface LenderUserQuery {
  /** the lender enum (Note that for multi-market ones, it is not the lender but the group)  */
  lender: any
  /** user address */
  account: string
  /** custom parameters for fetching e.g. multi-market lenders */
  params?: any[]
  /** custom parameters for spceifying assets */
  assets?: any[]
}

/**
 * Collapse per-market queries whose builder + RPC call are identical
 * across every market key into a single query. Without this, a chain
 * with N Gearbox CMs (or N Morpho markets) would fan out into N
 * identical RPC calls — all returning the same owner-scoped data.
 *
 * - **Morpho** (`MORPHO_BLUE_*` / `LISTA_DAO_*`): one query per family,
 *   `params` carries the original per-market lender keys.
 * - **Gearbox V3** (`GEARBOX_V3_*`): one `Lender.GEARBOX_V3` query per
 *   chain; `AccountCompressor.getCreditAccounts` already scopes by
 *   `configurators` + `owner`, so we only need to run the call once.
 *   `params` carries the requested per-CM lender keys in case the
 *   parser wants to emit empty buckets for CMs the user has no CAs
 *   in (currently it only emits populated ones — same outcome, but
 *   without the 24-duplicate-RPC-call penalty).
 */
export function organizeUserQueries(queries: LenderUserQuery[]) {
  const morphos = queries.filter((q) => isMorphoType(q.lender))
  const gearbox = queries.filter((q) => isGearboxV3(q.lender))
  // Morpho Midnight: per-market keys `MORPHO_MIDNIGHT_<id>` all resolve to the
  // same owner-scoped read set (buildMidnightUserCall already walks every market
  // per query), so N market keys would fan out to N × N-market calls. Collapse to
  // ONE `MORPHO_MIDNIGHT` query — mirrors the Morpho/Gearbox dedup above.
  const midnight = queries.filter((q) => isMidnight(q.lender))
  // Term Finance: per-repo keys `TERM_FINANCE_<id>` all resolve to the same
  // owner-scoped read set (buildTermUserCall walks every repo per query), so
  // collapse to ONE `TERM_FINANCE` query — mirrors the Midnight dedup below.
  const term = queries.filter((q) => isTerm(q.lender))
  // Exactly: per-market keys `EXACTLY_<MARKET_ADDR>` all resolve to the SAME
  // single Previewer.exactly(account) call, so collapse to ONE `EXACTLY` query.
  const exactly = queries.filter((q) => isExactly(q.lender))
  // Liquity family: per-branch keys `<KEY>_<chainId>_<COLL_INDEX>` all resolve to the
  // same owner-scoped read set (buildLiquityUserCall walks every branch per
  // query), so collapse to ONE query PER DEPLOYMENT (each family member keeps
  // its own bare key — fork brands share no prefix).
  const liquity = queries.filter((q) => isLiquityFamily(q.lender))
  // River: per-market keys `RIVER_<chainId>_<TM_INDEX>` all resolve to the same
  // account-scoped read set — collapse to ONE `RIVER` query.
  const river = queries.filter((q) => isRiver(q.lender))
  // Teller: per-pool keys `TELLER_<POOL_ADDR>` all resolve to the same
  // owner-scoped read set (buildTellerUserCall discovers every bidId via
  // `getBorrowerActiveLoanIds` and attributes it to a pool), so N pool keys
  // would fan out to N identical discovery passes. Collapse to ONE `TELLER`
  // query — mirrors the Exactly/River dedup above.
  const teller = queries.filter((q) => isTeller(q.lender))
  // TermMax: per-market keys `TERMMAX_<MARKET_ADDR>` all resolve to the SAME
  // owner-scoped read set — `buildTermMaxUserCall` batches every live market on
  // the chain into ONE `TermMaxViewer.getPositionDetails` call — so N market
  // keys would fan out to N identical N-market viewer calls. Collapse to ONE
  // `TERMMAX` query; same dedup as Exactly/Teller above. This matters more here
  // than elsewhere: Ethereum alone carries ~19 live markets.
  const termMax = queries.filter((q) => isTermMax(q.lender))
  // dss (Sky + the USDD fork): per-ilk keys `<BRAND>_<chainId>_<ILK>` all
  // resolve to the SAME owner-scoped CDP walk (buildDssUserCall discovers
  // every CDP via the manager's per-owner list), so N ilk keys would fan out
  // to N identical discovery passes. Collapse to ONE query PER BRAND —
  // Teller/River dedup. The two brands must NOT collapse into each other:
  // they are independent Maker stacks with their own CDP managers, so one
  // walk cannot answer for the other.
  const usdd = queries.filter((q) => isUsdd(q.lender))
  const sky = queries.filter((q) => isSky(q.lender))
  // LlamaLend: per-market keys `LLAMALEND_<CONTROLLER_ADDR>` all resolve to the
  // SAME owner-scoped read set — `buildLlamaLendUserCall` walks the whole
  // market roster per query — so N market keys would fan out to N identical
  // N-market walks. Collapse to ONE `LLAMALEND` query; same dedup as
  // Exactly/Teller/TermMax. This matters here as much as it does for TermMax:
  // Ethereum alone carries ~51 live markets.
  const llamaLend = queries.filter((q) => isLlamaLend(q.lender))
  /**
   * Silo: per-pair keys `SILO_V2_<CONFIG>` / `SILO_V3_<CONFIG>` — 122 of them on
   * Ethereum, 85 on Arbitrum. Unlike the families above, these are NOT redundant
   * reads of one owner-scoped set: each key is a genuinely separate market with
   * its own ten calls. Collapsing them is what lets ONE `PositionLens` call cover
   * the whole roster; left as 122 queries, the lens would be invoked 122 times and
   * cost more than the per-pair walk it replaces.
   *
   * The two versions stay separate keys — they are independent registries and one
   * roster cannot answer for the other — but each collapses to a single query
   * carrying its pair keys in `params`, which the builder and converter use as the
   * roster and the publish keys.
   */
  const siloV2 = queries.filter((q) => isSiloV2Type(q.lender))
  const siloV3 = queries.filter((q) => isSiloV3Type(q.lender))
  // Frankencoin: per-original keys all resolve to the SAME owner-scoped
  // discovery (one cached book fetch covers every market), so N market keys
  // would fan out to N identical passes. Collapse to ONE query.
  const frankencoin = queries.filter((q) => isFrankencoin(q.lender))
  if (
    morphos.length === 0 &&
    gearbox.length === 0 &&
    midnight.length === 0 &&
    term.length === 0 &&
    exactly.length === 0 &&
    liquity.length === 0 &&
    river.length === 0 &&
    teller.length === 0 &&
    termMax.length === 0 &&
    usdd.length === 0 &&
    sky.length === 0 &&
    frankencoin.length === 0 &&
    llamaLend.length === 0 &&
    siloV2.length === 0 &&
    siloV3.length === 0
  )
    return queries

  const others = queries.filter(
    (q) =>
      !isMorphoType(q.lender) &&
      !isGearboxV3(q.lender) &&
      !isMidnight(q.lender) &&
      !isTerm(q.lender) &&
      !isExactly(q.lender) &&
      !isLiquityFamily(q.lender) &&
      !isRiver(q.lender) &&
      !isTeller(q.lender) &&
      !isTermMax(q.lender) &&
      !isDssType(q.lender) &&
      !isFrankencoin(q.lender) &&
      !isLlamaLend(q.lender) &&
      !isSiloV2Type(q.lender) &&
      !isSiloV3Type(q.lender),
  )

  const morphoBlue = morphos.filter((q) => q.lender.startsWith('MORPHO_BLUE'))
  const moolah = morphos.filter((q) => q.lender.startsWith('LISTA_DAO'))

  const result = [...others]

  if (morphoBlue.length > 0) {
    result.push({
      lender: Lender.MORPHO_BLUE,
      account: morphoBlue[0].account,
      params: morphoBlue.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (moolah.length > 0) {
    result.push({
      lender: Lender.LISTA_DAO,
      account: moolah[0].account,
      params: moolah.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (gearbox.length > 0) {
    result.push({
      lender: Lender.GEARBOX_V3,
      account: gearbox[0].account,
      params: gearbox.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (midnight.length > 0) {
    result.push({
      lender: Lender.MORPHO_MIDNIGHT,
      account: midnight[0].account,
      params: midnight.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (term.length > 0) {
    result.push({
      lender: Lender.TERM_FINANCE,
      account: term[0].account,
      params: term.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (exactly.length > 0) {
    result.push({
      lender: Lender.EXACTLY,
      account: exactly[0].account,
      params: exactly.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (river.length > 0) {
    result.push({
      lender: Lender.RIVER,
      account: river[0].account,
      params: river.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (frankencoin.length > 0) {
    result.push({
      lender: Lender.FRANKENCOIN,
      account: frankencoin[0].account,
      params: frankencoin.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (usdd.length > 0) {
    result.push({
      lender: Lender.USDD,
      account: usdd[0].account,
      params: usdd.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (sky.length > 0) {
    result.push({
      lender: Lender.SKY,
      account: sky[0].account,
      params: sky.map((p) => p.lender),
      assets: undefined,
    })
  }

  // The bare prefixes are valid lender keys for `isSiloV2Type`/`isSiloV3Type`,
  // and the roster travels in `params`.
  if (siloV2.length > 0) {
    result.push({
      lender: 'SILO_V2' as Lender,
      account: siloV2[0].account,
      params: siloV2.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (siloV3.length > 0) {
    result.push({
      lender: 'SILO_V3' as Lender,
      account: siloV3[0].account,
      params: siloV3.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (llamaLend.length > 0) {
    result.push({
      lender: Lender.LLAMALEND,
      account: llamaLend[0].account,
      params: llamaLend.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (teller.length > 0) {
    result.push({
      lender: Lender.TELLER,
      account: teller[0].account,
      params: teller.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (termMax.length > 0) {
    result.push({
      lender: Lender.TERMMAX,
      account: termMax[0].account,
      params: termMax.map((p) => p.lender),
      assets: undefined,
    })
  }

  if (liquity.length > 0) {
    for (const base of LIQUITY_FAMILY_LENDERS) {
      const members = liquity.filter((q) => q.lender.startsWith(base))
      if (members.length === 0) continue
      result.push({
        lender: base,
        account: members[0].account,
        params: members.map((p) => p.lender),
        assets: undefined,
      })
    }
  }

  return result
}

export interface UserLendingPosition {
  // token amounts
  deposits: string
  debt: string
  debtStable: number
  // usd amounts
  depositsUSD: number
  debtUSD: number
  debtStableUSD: number

  collateralEnabled: boolean
  claimableRewards: number
}

export type UserRewardEntry = {
  asset: string
  totalRewards: number
  claimableRewards: number
}[]

export interface LenderUserResponse {
  chainId: string
  account: string
  lendingPositions: {
    [lender: string]: {
      [marketUid: string]: UserLendingPosition
    }
  }
  rewards: UserRewardEntry
}

// Aave V2 specific enums
export enum AaveV2TypeGetUserReserveData {
  currentATokenBalance = 0,
  currentStableDebt,
  currentVariableDebt,
  principalStableDebt,
  scaledVariableDebt,
  stableBorrowRate,
  liquidityRate,
  stableRateLastUpdated,
  usageAsCollateralEnabled,
}

// Aave V3 specific enums
export enum AaveV3TypeGetUserReserveData {
  currentATokenBalance = 0,
  currentStableDebt,
  currentVariableDebt,
  principalStableDebt,
  scaledVariableDebt,
  stableBorrowRate,
  liquidityRate,
  stableRateLastUpdated,
  usageAsCollateralEnabled,
}

///////
export interface AaveV2UserReserveResponse {
  chainId: string
  account: string
  lendingPositions: {
    [marketUid: string]: BaseLendingPositions
  }
  rewards: UserRewardEntry
}

export interface CompoundV2UserReserveResponse extends AaveV2UserReserveResponse {}

export interface BaseLendingPositions {
  marketUid: string
  /**
   * Optional loan binding. Absent ⇒ the position applies to ALL loans (e.g. shared collateral);
   * present ⇒ the position is strictly tied to that loan. (Distinct from sub-account `accountId`.)
   */
  loanId?: string
  // token amounts
  deposits: string
  debt: string
  debtStable: string
  // usd amounts (general price - for display)
  depositsUSD: number
  debtUSD: number
  debtStableUSD: number
  // usd amounts (oracle price - for risk calculations)
  depositsUSDOracle?: number
  debtUSDOracle?: number
  debtStableUSDOracle?: number

  collateralEnabled: boolean
  claimableRewards: number

  // max token amounts preserving safe health factor (1.01)
  /** max withdrawable token amount (capped at deposit balance) */
  withdrawable?: string
  /** max borrowable token amount */
  borrowable?: string
  /** remaining deposit capacity in token units (null = uncapped) */
  depositable?: string

  /** Underlying asset info (asset metadata, prices, oracle price) */
  underlyingInfo?: {
    asset: any
    oraclePrice?: any
    prices: any
  }

  // Silo-specific: standard (borrowable) vs protected (non-borrowable) collateral
  /** Standard collateral deposit amount (borrowable, accrues interest) */
  depositsStandard?: string
  /** Protected collateral deposit amount (non-borrowable, no interest accrual) */
  depositsProtected?: string
  /** Collateral share exchange rate: assets per 10^decimals shares */
  collateralRate?: string
}

/**
 * Early-repayment policy for a fixed-term market. `none` = a borrower can exit
 * any time at the current market price with no penalty (Morpho Midnight buys the
 * debt units back on the order book). `penalty` = a per-loan penalty applies
 * (Lista); the concrete amount is position-level, on `ListaTermLoan.earlyRepayPenalty`.
 * `discount` = repaying early costs LESS than face value (Exactly: the pool's
 * unassigned earnings are rebated to the early repayer — the exact amount comes
 * from `previewRepayAtMaturity` at repay time). Note that a `discount` lender
 * can still have a LATE-repay penalty (see `fees.latePenaltyApr`).
 */
export type FixedTermEarlyRepay = { kind: 'none' | 'penalty' | 'discount' }

/**
 * Who fronts a fixed term — the cross-protocol answer to "who is offering these
 * borrow/lend terms". Consistent across lenders even though the shape differs:
 *  - `broker`: a single market broker sets the term (Lista LendingBroker proxy).
 *    `address` is that broker contract — one per market, stable, public.
 *  - `orderbook`: the term is an aggregate of many signed maker offers (Morpho
 *    Midnight) or continuous repo-token listings (Term Finance secondary
 *    market). There is no single provider at the market level, so `address` is
 *    omitted; the concrete maker(s) are per-offer and only known at quote time.
 *  - `auction`: the term is discovered by a periodic sealed-bid auction (Term
 *    Finance primary market). `address` is the per-repo auction/servicer venue.
 *  - `pool`: the term is fronted by a passive liquidity pool with a
 *    utilization-curve rate (Exactly fixed pools backed by the floating pool).
 *    `address` is the Market contract.
 */
export interface FixedTermProvider {
  kind: 'broker' | 'orderbook' | 'auction' | 'pool'
  /** The single counterparty/venue contract, when there is one (Lista broker, Term servicer). */
  address?: string
}

/**
 * Origination window for a fixed-term market whose terms are only obtainable
 * during a bounded round rather than continuously (`provider.kind: 'auction'`
 * — Term Finance).
 *
 * This is the difference between "the rate card is empty right now" and "this
 * market is dead": between rounds a Term repo still has a maturity, collateral
 * params and a last-cleared rate, but nothing can be borrowed until the next
 * round is listed. Without it every closed repo renders as an ordinary
 * borrowable market whose action silently cannot be built.
 *
 * `status` is a snapshot at fetch time; the timestamps are raw so a consumer
 * can re-derive it live (and drive a countdown) against a cached response.
 */
export interface FixedTermAuction {
  /**
   * Round lifecycle at fetch time:
   *  - `upcoming`  — listed but not yet accepting submissions (`now < startTime`)
   *  - `open`      — accepting sealed bids/offers (`startTime ≤ now < revealTime`)
   *  - `revealing` — submissions closed, prices revealing / clearing pending
   *                  (`revealTime ≤ now < endTime`)
   *  - `closed`    — no round is currently listed for this market. Borrowing is
   *                  unavailable until the next one; lending may still be
   *                  possible on the secondary repo-token book.
   */
  status: 'upcoming' | 'open' | 'revealing' | 'closed'
  /**
   * Can a NEW borrow be opened right now? True only inside an open round —
   * Term borrow origination is a sealed bid, so there is no other entry point.
   *
   * Consume this rather than re-deriving from `status`: it is the single flag
   * a borrow CTA should gate on, and it stays correct if more statuses appear.
   * It is NOT the same as `canLend` — see below.
   */
  canBorrow: boolean
  /**
   * Can a NEW lend position be opened right now? Deliberately decoupled from
   * `canBorrow`: the primary auction is only one of two lend surfaces, and
   * buying repo tokens on the secondary book works between rounds. So a closed
   * round leaves the market lend-only rather than fully inert, and a UI that
   * greys out the whole market would be wrong.
   */
  canLend: boolean
  /**
   * Seconds until submissions close (`revealTime − now`), or undefined when no
   * round is open. A snapshot — for a live countdown, derive from `revealTime`.
   */
  secondsUntilClose?: number
  /**
   * Ready-to-display consequences of this market's origination model, most
   * important first. Mirrors `params.market.teller.implications`: auction
   * mechanics are unusual enough that a UI showing only a rate misleads.
   */
  implications?: string[]
  /** Round id. Absent when `status: 'closed'`. */
  id?: string
  /** Submissions open (unix seconds). Absent when `status: 'closed'`. */
  startTime?: number
  /** Submissions CLOSE / reveal begins (unix seconds). Absent when closed. */
  revealTime?: number
  /** Round clears (unix seconds). Absent when closed. */
  endTime?: number
  /**
   * Minimum submission size in loan-token base units (raw). Term rounds carry a
   * real floor (e.g. 1000 USDC) — an amount below it cannot be submitted at all,
   * so it belongs next to the terms rather than surfacing as a failed action.
   */
  minBorrowAmount?: string
  minLendAmount?: string
}

/**
 * Canonical fixed-term market descriptor, emitted on `params.market.fixedTerm`
 * for EVERY fixed-rate / fixed-maturity market (Lista brokered + Morpho
 * Midnight) so consumers read ONE shape instead of branching per protocol. The
 * rate-card menu stays on `params.market.terms`; this carries the cross-protocol
 * maturity + fee + early-repay facts. Absent on non-fixed-term markets.
 */
export interface FixedTermInfo {
  /** Underlying fixed-term protocol shape. */
  model: 'lista' | 'midnight' | 'term' | 'exactly' | 'teller' | 'termmax'
  /**
   * Single fixed calendar maturity (unix secs). Undefined for rolling-duration
   * menus (Lista) and multi-maturity markets (Exactly — the maturity menu lives
   * on `params.market.terms`, keyed by `termId` = maturity timestamp).
   */
  maturity?: number
  /** Market-level fees. Empty for lenders without them (Lista); values are 0 when genuinely off. */
  fees: {
    /** Continuous fee, %/yr — ongoing lender-side haircut (Midnight). */
    continuousFeeApr?: number
    /** Settlement fee as a fraction at the current time-to-maturity (Midnight). */
    settlementFee?: number
    /**
     * LATE-repayment penalty, %/yr, accruing per second on overdue debt after
     * maturity until repaid (Exactly `penaltyRate`). Absent for lenders whose
     * overdue handling is liquidation-only (Midnight/Term).
     */
    latePenaltyApr?: number
    /**
     * UPFRONT origination fee as a percent of the borrowed principal, charged
     * once at borrow time (Teller: market fee + protocol fee). Not an APR.
     */
    originationFeePercent?: number
  }
  /** Early-repayment policy. */
  earlyRepay: FixedTermEarlyRepay
  /** Who offers the term (Lista broker vs Midnight order book). */
  provider?: FixedTermProvider
  /**
   * Origination window, for `provider.kind: 'auction'` markets only (Term
   * Finance). Absent for lenders whose terms are continuously available — a
   * missing `auction` means "no window applies", NOT "closed".
   *
   * Where a FILL-NOW surface also exists (Term Terminal 1 limit orders),
   * `canBorrow`/`canLend` reflect ALL entry paths — they can be true while
   * `status` is `closed`. `fillNow` below carries the instant-path detail.
   */
  auction?: FixedTermAuction
  /**
   * Instant (limit-order) origination liquidity, where the lender has one
   * (Term Finance Terminal 1). Unlike the auction, these rates are obtainable
   * at fill time: a taker settles a maker's standing order at the order's own
   * rate. Absent = the lender has no fill-now surface or none is live.
   */
  fillNow?: FixedTermFillNow
}

/**
 * Fill-now (limit-order) origination summary for a fixed-term market. Rates
 * are best-executable percents in the lender's own day-count convention;
 * liquidity is loan-token assets (human-scaled). The per-level book lives on
 * `params.market.book` — this is the CTA-gating summary.
 */
export interface FixedTermFillNow {
  /** A NEW borrow can be filled instantly against standing lend orders. */
  canBorrow: boolean
  /** A NEW lend can be filled instantly against standing borrow orders. */
  canLend: boolean
  /** Best instantly-executable borrow APR, percent. */
  borrowAprPct?: number
  /** Best instantly-executable lend APR, percent. */
  lendAprPct?: number
  /** Instantly-borrowable depth, loan-token assets. */
  borrowLiquidity?: number
  /** Instantly-lendable depth, loan-token assets. */
  lendLiquidity?: number
}

/**
 * A single fixed-term loan, attached to its own entry in the positions array.
 *
 * Named for Lista (the first producer) but SHARED by every fixed-term lender
 * that emits per-loan rows — Lista, Exactly, TermMax, Teller. Fields are
 * therefore mostly optional and several are lender-specific; see
 * [FIXED_TERM_REPAY_TERMS.md](../../../../FIXED_TERM_REPAY_TERMS.md) for which
 * lender populates what and for the exact repay economics behind each number.
 *
 * `loanId` is the WRITE TARGET and its meaning differs per lender (Lista posId /
 * Exactly maturity-as-string / TermMax gtId / Teller bidId) — check the lender
 * before using it. `termId` is the RATE-MENU id and is NOT interchangeable with
 * it (Exactly is the only lender where the two coincide, both = maturity).
 */
export interface ListaTermLoan {
  /** loanId — the repay target for the LISTA_BROKER_REPAY composer op. For fixed loans this is the
   *  posId; for the dynamic (flexible) loan it is the dynamic sentinel (type(uint128).max).
   *  Other lenders reuse the slot: Exactly = String(maturity), TermMax = gtId, Teller = bidId. */
  loanId: string
  /** true for the flexible (dynamic / variable-rate) loan; fixed loans omit it */
  isDynamic?: boolean
  /** best-effort term product id (matched from duration vs the current menu); may be undefined */
  termId?: number
  /** outstanding debt in loan-token units. Lista: principal + accrued interest.
   *  Static-face-value lenders (Exactly / TermMax / Term): the EXIT-NOW cost —
   *  for Exactly that is discounted early and penalty-inflated when overdue, so
   *  compare against `faceValue` rather than assuming it is the face. */
  debt: string
  /** locked annualised borrow rate in PERCENT (e.g. 3.857 = 3.857% APR) */
  apr?: number
  /** unix maturity timestamp */
  maturity?: number
  termDays?: number
  /** outstanding accrued interest in loan-token units */
  accruedInterest?: string
  /** early-repayment penalty (loan-token units) to close the loan now; 0 once matured.
   *  Lista only — the OPPOSITE sign to Exactly's `earlyRepayDiscount` below. */
  earlyRepayPenalty?: string
  isMatured?: boolean
  // --- static-face-value fixed-term detail (Exactly today) ---------------
  /** amount owed AT maturity (principal + fee). Static — no accrual index; it
   *  grows only via a late penalty where the protocol has one. */
  faceValue?: string
  /** Exactly: rebate for repaying BEFORE maturity (`faceValue − debt`). Exactly
   *  never charges an early-repay fee, but this is 0 when the fixed pool has no
   *  unassigned earnings left, so it is not a guaranteed saving. */
  earlyRepayDiscount?: string
  /** Exactly: penalty accrued so far past maturity (`debt − faceValue`). */
  latePenalty?: string
  /** Exactly: further penalty per additional day overdue — LINEAR on face, not
   *  compounding. */
  latePenaltyPerDay?: string
  /** annualized late-penalty rate in PERCENT (Exactly `penaltyRate`; ~164 %/yr).
   *  A mutable market parameter, snapshotted per fetch. */
  latePenaltyApr?: number
  /** seconds past maturity; 0 until overdue */
  secondsLate?: number
}

interface MorphoLendingPositions extends BaseLendingPositions {
  isWhitelisted?: boolean
  /**
   * Lista fixed-term loan detail. A brokered market emits, under the same accountId, one extra
   * positions[] entry per fixed-term loan carrying this `term` (with termId + term data); its
   * debt sits in `debtStable` and `loanId` is the repay target. The aggregate loan position
   * (no `term`) carries the rollup `debtStable` total for health.
   */
  term?: ListaTermLoan
}

export interface AaveV3UserReserveResponse {
  chainId: string
  lendingPositions: {
    [marketUid: string]: BaseLendingPositions
  }
  userEMode: number
  rewards?: any
  account: string
}

export interface MorphoUserReserveResponse {
  chainId: string
  lendingPositions: {
    [marketUid: string]: MorphoLendingPositions
  }
  id: string
  rewards?: any
  account: string
}

export interface CompoundV3UserReserveResponse {
  lendingPositions: {
    [marketUid: string]: {
      // tokens
      deposits: string
      debt: string
      debtStable: string
      // dollars
      depositsUSD: number
      debtUSD: number
      debtStableUSD: number

      collateralEnabled: boolean
      isAllowed: boolean
    }
  }
  chainId: string
  baseAsset: string
  rewards: UserRewardEntry
  account: string
}

export interface AaveV3Public extends LenderPublicBase {
  lastUpdateTimestamp?: number

  // flags of reserve state
  collateralActive?: boolean
  hasStable?: boolean
  isActive?: boolean
  isFrozen?: boolean

  // emode info
  // each eMode category has a custom ltv and liquidation threshold
  eMode?: EModeData

  // limits
  debtCeiling: number
  supplyCap: number
  borrowCap: number

  config: { [modeId: string]: LenderConfigData }
}

/**
 * Interface shared by all lenders
 */
export interface LenderPublicBase extends LenderYields, LenderTotalAmounts {
  // borrow enabled flag
  borrowingEnabled: boolean
  // deposit enabled flag
  depositsEnabled: boolean

  /**
   * Max fraction of a borrower's debt repayable in one liquidation (0..1).
   * Protocol-level constant for most lenders (Aave `0.5`, Compound V2
   * `closeFactorMantissa`); `1` (full liquidation) for isolated /
   * credit-account protocols. Defaults to `1` when not applicable.
   */
  closeFactor: number

  /**
   * Liquidation target health factor (e.g. 1.05) for protocols that liquidate
   * to a target HF rather than by a fixed close factor (Aave V4, spoke-level).
   * `undefined` where the protocol has no such parameter.
   */
  targetHealthFactor?: number

  config: LenderConfigMap
}

export interface LenderYields extends BaseYields {
  // intrinsic yield (e.g. staking, LST rebasing)
  intrinsicYield: number

  // rewards
  rewards?: RewardsList
}

export interface LenderTotalAmounts {
  // debt totals
  totalDebt: number
  totalDebtStable: number
  // deposit total
  totalDeposits: number
  // liquidity
  totalLiquidity: number

  /** USD values */

  // debt totals
  totalDebtUSD: number
  totalDebtStableUSD: number
  // deposit total
  totalDepositsUSD: number
  // liquidity
  totalLiquidityUSD: number
}

export interface LenderConfigMap {
  [modeId: string]: LenderConfigData
}

/**
 * The Mode-specific configuration for a lender asset
 */
export interface LenderConfigData {
  label?: string
  category: number
  borrowCollateralFactor: number
  collateralFactor: number
  borrowFactor: number
  /**
   * Liquidation penalty for this mode, as a fraction of repaid debt the
   * liquidator receives on top of par (e.g. `0.05` = 5% bonus). Mode-specific
   * where the protocol supports it (Aave e-modes, Dolomite categories, Euler
   * vaults); `0` when not liquidatable in this mode or unsupported.
   */
  liquidationPenalty: number
  /**
   * Max fraction of debt repayable in a single liquidation (0..1). Mirrors the
   * pool-level {@link LenderPublicBase.closeFactor}; identical across every mode
   * of a market (no supported protocol varies close factor by e-mode).
   */
  closeFactor: number
  /**
   * Liquidation target health factor (e.g. 1.05) for protocols that liquidate
   * to a target HF rather than by a fixed close factor (Aave V4, spoke-level).
   * Mirrors {@link LenderPublicBase.targetHealthFactor}; `undefined` otherwise.
   */
  targetHealthFactor?: number
  collateralDisabled?: boolean
  debtDisabled?: boolean
  /**
   * Borrower-chosen open-time parameter this config's numbers depend on.
   * Mirrors the API-side `ConfigEntry.openParameter`; see
   * POSITION_PARAMETERS_PLAN.md.
   */
  openParameter?: OpenParameter
}

export interface ModeBase {
  category: number
  label: string
}

export interface EModeData extends ModeBase {
  borrowCollateralFactor: number
  collateralFactor: number
  priceSource: string
}

export interface AaveV2Public extends LenderPublicBase {
  // reserve market data
  lastUpdateTimestamp?: number

  // reserve config
  decimals?: number
  reserveFactor?: SerializedBigNumber
  collateralActive?: boolean
  hasStable?: boolean

  // frozen
  isActive?: boolean
  isFrozen?: boolean
}

export interface CompoundV3Public extends LenderPublicBase {
  supplyCap: number
  collateralActive: boolean
  utilization: number
}

export interface UserApr {
  apr: number
  borrowApr: number
  depositApr: number
}

export interface InitUserReserveResponse {
  chainId: string
  lendingPositions: {
    // positionId is the NFT id
    [posId: string]: {
      [marketUid: string]: {
        deposits: string
        debt: string
        depositsUSD: number
        debtUSD: number
        debtStable: '0'
        debtStableUSD: 0
        collateralEnabled: boolean
        isAllowed: boolean
      }
    }
  }
  /** Mode per position ID, separated from positions for clean array serialization */
  modes: { [posId: string]: any }
  account: string
}

export interface InitPublic extends LenderPublicBase {
  // flags of reserve state
  collateralActive?: boolean

  // limits
  debtCeiling: number
  supplyCap: number
  borrowCap: number
}
